import { type CSSProperties, useLayoutEffect, useRef, useState } from "react";

/** Which edge of its containing block the frozen anchor is pinned to. */
type AnchorPin = "left" | "right";

/**
 * Freezes an open popover's position while its trigger keeps resizing underneath it.
 *
 * Radix glues the popover to its trigger with floating-ui's `autoUpdate`, which observes the
 * reference element's size — so ticking a row in a multi-select repositions the open popover the
 * instant the trigger grows a badge row or a wider label. Radix hardcodes `autoUpdate`'s options
 * (only `updatePositionStrategy` is exposed) and declined to add a knob for this, so the anchor is
 * the only lever: the popover is pointed at an invisible element that holds the trigger's box as
 * it was when the popover opened.
 *
 * Because that element is really in the DOM, alongside the trigger and inside the same containing
 * block, scrolling at every level and any movement of the surrounding container carry it for free —
 * there is no rect to recompute and no scroll offset to track.
 *
 * **Freeze the anchor, never the trigger.** Content, labels, badges, counts, width, height and
 * layout all stay live; nothing here constrains how the trigger renders.
 *
 * Attach `triggerRef` to the trigger and spread `style` onto a `<PopoverAnchor asChild>` span
 * rendered INSIDE the containing block — with no `containerRef`, that is the trigger itself, so
 * the span goes among the trigger's own children (a sibling of the trigger would be positioned
 * against some unrelated ancestor). The span is absolutely positioned, so it is out of flow: it
 * takes no space and no `gap`, and does not become a flex item.
 *
 * ```tsx
 * const anchor = useAnchorSnapshot<HTMLDivElement>(isOpen);
 * <PopoverTrigger asChild>
 *   <SelectTrigger ref={anchor.triggerRef} className={cn("relative", className)}>
 *     {…live trigger content…}
 *     {anchor.style && (
 *       <PopoverAnchor asChild>
 *         <span aria-hidden className="pointer-events-none" style={anchor.style} />
 *       </PopoverAnchor>
 *     )}
 *   </SelectTrigger>
 * </PopoverTrigger>
 * ```
 *
 * Radix HoverCard exports no `Anchor` part and keeps its popper scope module-private, so this
 * covers Popover only — a hover card has to keep tracking its trigger.
 *
 * @param open - Whether the popover is open. Only the opening edge measures.
 * @param options.pin - Which edge to hold still when the trigger is its own containing block.
 *   `"right"` where the trigger sits in a right-anchored (`ml-auto` / `justify-end`) group, whose
 *   growth pushes the LEFT edge outward while the right edge stays put. Ignored once
 *   `containerRef` is attached, since the trigger's own offsets are then absolute.
 * @returns `triggerRef` for the trigger, `containerRef` for an ancestor `position: relative` box
 *   (optional — leave it unattached to use the trigger itself), and the anchor's inline `style`,
 *   `undefined` until the first opening.
 */
export function useAnchorSnapshot<
  TTrigger extends HTMLElement = HTMLElement,
  TContainer extends HTMLElement = HTMLElement,
>(open: boolean, { pin = "left" }: { pin?: AnchorPin } = {}) {
  const triggerRef = useRef<TTrigger | null>(null);
  const containerRef = useRef<TContainer | null>(null);
  const [style, setStyle] = useState<CSSProperties>();

  useLayoutEffect(() => {
    // Measured on opening and never cleared. `PopoverContent` has no `forceMount`, so Presence
    // keeps it mounted through the close animation with `open` already false — dropping the box
    // there would re-anchor it to the grown trigger and jump it mid-fade. The next opening
    // overwrites it, which is what makes a reopened popover read the trigger's latest layout.
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    // `offset*` is already expressed in the containing block's coordinates — exactly what
    // `position: absolute` consumes — so there is nothing to convert. `offset*` measures the
    // BORDER box while `left`/`top: 0` start at the containing block's PADDING box, so a trigger
    // that is its own containing block must not carry a border, or the box lands a border-width
    // off. None of the current call sites does (`SelectTrigger` outlines rather than borders).
    const size = { width: trigger.offsetWidth, height: trigger.offsetHeight };
    if (containerRef.current) {
      setStyle({
        position: "absolute",
        left: trigger.offsetLeft,
        top: trigger.offsetTop,
        ...size,
      });
      return;
    }
    setStyle(
      pin === "right"
        ? { position: "absolute", top: 0, right: 0, ...size }
        : { position: "absolute", top: 0, left: 0, ...size },
    );
  }, [open, pin]);

  return { triggerRef, containerRef, style };
}
